/**
 * ImmersiveTranslate — page-wide bilingual rendering.
 *
 * UX (common pattern for inline page-translation tooling):
 *   1. Host calls `translate.enable('zh-TW')` (usually from a palette command).
 *   2. Module walks the visible DOM, collects translatable text BLOCKS (one
 *      per block-level element — paragraph, heading, list item, etc.).
 *   3. Blocks are BATCHED into LLM calls (default 30 blocks per call, ~5k
 *      chars per call cap) — so a 200-paragraph article costs ~7 API calls,
 *      not 200.
 *   4. For each translated block, we append a styled inline element AFTER
 *      the original (NOT replacing). Default wrapper is `<font class=
 *      "dddk-imm-translate">` — a deliberate use of the legacy <font> tag
 *      because it survives most rich-text editors and PDF viewers without
 *      being styled away by host CSS, which is the conventional choice for
 *      this kind of inline-translation overlay.
 *   5. Cache by content hash so re-enabling the same language is instant.
 *
 * Limitations:
 *   - PDFs: HTML viewers (pdf.js / Chrome built-in) render selectable text
 *     in an overlay — this module will translate that overlay if it's part
 *     of the DOM; native PDF viewers (the browser plugin) are out of reach.
 *   - docx: native .docx files served as downloads are not in the DOM.
 *     If your host renders docx as HTML (e.g. via mammoth.js), it works.
 *     If you embed Office Online viewer, the iframe is cross-origin → out
 *     of reach.
 *   - Both PDF and docx have to be HTML in the page for this module to
 *     touch them. The output convention (`<font class="dddk-imm-translate">`)
 *     was chosen so downstream PDF / docx renderers that DO accept inline
 *     HTML preserve the bilingual layout.
 */

import { resolveLLM, type LLMSource } from '../../agent/llm/router';
import type { DotDotDuck } from '../../orchestrator';
import { stripCodeFence } from '../../utils/llm-parse';
import { injectScopedStyle } from '../../utils/dom';

const TRANSLATED_ATTR = 'data-dddk-imm-translated';
const WRAPPER_TAG = 'font';                         // see file header
const WRAPPER_CLASS = 'dddk-imm-translate';
const STYLE_ID = 'dddk-imm-translate-style';

export interface ImmersiveTranslateConfig {
  llm: LLMSource;
  /** Max DOM blocks per LLM call. Default 30. */
  batchSize?: number;
  /** Max combined chars per LLM call. Default 4500. Batches break here too. */
  batchCharCap?: number;
  /** CSS selectors whose contents should NOT be translated. */
  ignoreSelector?: string;
  /** Root element to walk. Default `document.body`. */
  root?: HTMLElement;
  /** Optional persisted cache. Falls back to in-memory Map. */
  cache?: { get(key: string): string | null; set(key: string, val: string): void };
}

const DEFAULT_IGNORE = [
  '[data-dddk-ui]',
  '[data-dddk-no-translate]',
  'script', 'style', 'noscript', 'svg', 'code', 'pre', 'kbd',
  '.dddk-imm-translate',
];

// Translatable block tags. `div` is included because many design systems
// (Tailwind UI, custom card components, SvelteKit pages, etc.) use bare
// `<div>` for card titles / bodies / sections instead of semantic `<p>` /
// `<h*>` — without `div` we silently skip those texts. The walker's
// "skip if contains a block child" rule (see `collectBlocks`) prevents
// double-translation of div-wrapped paragraphs, since the outer div is
// SKIPped (descend) when its children are also blocks.
const BLOCK_TAGS = new Set([
  'p', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote',
  'figcaption', 'td', 'th', 'dt', 'dd', 'summary', 'caption',
  'div',
]);

// System prompt lives in `./prompt.ts` and is registered on the SDK's
// central prompt registry. Callsites below resolve via the registry so
// hosts can override per-locale without patching this file.
import { PROMPT_IDS, promptRegistry } from '../../prompts/registry';
import { registerBuiltinPrompts } from '../../prompts/defaults';

// Ensure defaults are registered on module load. Idempotent — safe to
// call from multiple entry points.
registerBuiltinPrompts();

export class ImmersiveTranslate {
  private cfg: Required<Pick<ImmersiveTranslateConfig, 'batchSize' | 'batchCharCap' | 'root'>>
    & Pick<ImmersiveTranslateConfig, 'llm' | 'ignoreSelector' | 'cache'>;
  private effectiveIgnore: string;
  private memCache = new Map<string, string>();
  private currentLang: string | null = null;
  private dddk: DotDotDuck | null = null;
  /** Teardown for the SPA route-change listener installed in `attachTo`. */
  private routeChangeTeardown: (() => void) | null = null;

  constructor(config: ImmersiveTranslateConfig) {
    this.cfg = {
      llm: config.llm,
      // Smaller batches than the original 30 / 4500. With Promise.all
      // fan-out, latency is bounded by the slowest single call —
      // generating 4500 chars of translation takes ~5s on a mid model,
      // generating 1200 chars takes ~1.5s. The total work is the same,
      // so more, smaller batches → much faster wall-clock on parallel
      // hardware. Hosts that hit rate limits can override upward.
      batchSize: config.batchSize ?? 8,
      batchCharCap: config.batchCharCap ?? 1200,
      root: config.root ?? (typeof document !== 'undefined' ? document.body : (null as unknown as HTMLElement)),
      ignoreSelector: config.ignoreSelector,
      cache: config.cache,
    };
    const parts = [...DEFAULT_IGNORE];
    if (config.ignoreSelector) parts.push(config.ignoreSelector);
    this.effectiveIgnore = parts.join(', ');
  }

  attachTo(dddk: DotDotDuck): void {
    this.dddk = dddk;
    ensureStyles();
    this.installRouteChangeCleanup();
  }

  /**
   * Wipe injected translation nodes on any SPA route change.
   *
   * Without this, a host SPA (SvelteKit / Next / Vue Router) swaps its
   * page slot but our `<font.dddk-imm-translate>` siblings stay attached
   * to the still-mounted parent nodes — they bleed into the next page,
   * making it look like two pages are stacked. We catch:
   *   - `popstate` (back / forward),
   *   - patched `history.pushState` / `replaceState` (programmatic nav).
   *
   * Idempotent — re-installing replaces the previous teardown so a host
   * calling `attachTo` twice doesn't accumulate listeners.
   */
  private installRouteChangeCleanup(): void {
    if (typeof window === 'undefined' || typeof history === 'undefined') return;
    this.routeChangeTeardown?.();

    let lastPath = window.location.pathname + window.location.search;
    const checkAndClear = (): void => {
      const now = window.location.pathname + window.location.search;
      if (now === lastPath) return;
      lastPath = now;
      if (this.currentLang) this.disable();
    };

    // Patch pushState / replaceState to dispatch a custom event we can
    // listen to. SvelteKit / Next / Vue Router all funnel SPA nav through
    // these; popstate alone doesn't catch programmatic navigation.
    const origPush = history.pushState;
    const origReplace = history.replaceState;
    const ROUTE_EVENT = 'dddk:imm-translate-route-change';
    history.pushState = function patchedPush(...args) {
      const r = origPush.apply(this, args);
      window.dispatchEvent(new Event(ROUTE_EVENT));
      return r;
    };
    history.replaceState = function patchedReplace(...args) {
      const r = origReplace.apply(this, args);
      window.dispatchEvent(new Event(ROUTE_EVENT));
      return r;
    };

    window.addEventListener('popstate', checkAndClear);
    window.addEventListener(ROUTE_EVENT, checkAndClear);

    this.routeChangeTeardown = () => {
      window.removeEventListener('popstate', checkAndClear);
      window.removeEventListener(ROUTE_EVENT, checkAndClear);
      history.pushState = origPush;
      history.replaceState = origReplace;
    };
  }

  isEnabled(): boolean { return this.currentLang !== null; }
  language(): string | null { return this.currentLang; }

  /** Run a full translation pass. If the language differs from the previous
   *  enable, the page is cleared and re-translated. */
  async enable(targetLang: string, label?: string): Promise<void> {
    if (this.currentLang === targetLang) return;
    if (this.currentLang) this.disable();
    this.currentLang = targetLang;
    ensureStyles();

    const blocks = this.collectBlocks();
    if (blocks.length === 0) return;

    // Persistent processing indicator so the bottom of the screen
    // shows "translating…" the entire time the LLM calls are in flight,
    // not just for the brief moment between `subtitle.show` re-renders.
    // Without this the user sees a flash of progress text then nothing
    // while the slowest batch finishes, and assumes the feature hung.
    const indicatorLabel = `Translating → ${label ?? targetLang}`;
    this.dddk?.subtitle.showIndicator('processing', indicatorLabel);
    this.dddk?.subtitle.show({
      text: `Translating → ${label ?? targetLang} (${blocks.length} blocks)`,
      type: 'info',
    });

    // Batch the blocks honouring both block-count and char-cap, then
    // fire every batch IN PARALLEL. Sequential awaits used to walk a
    // 27-block page through one batch at a time; with parallel fan-out
    // a typical doc page completes in roughly the slowest single batch's
    // latency. Each batch independently checks the cache + mounts results
    // so a slow / failing batch doesn't block the others.
    const batches = this.buildBatches(blocks);
    let done = 0;
    const updateProgress = (): void => {
      this.dddk?.subtitle.show({
        text: `Translating ${done}/${blocks.length} → ${label ?? targetLang}`,
        type: 'info',
      });
      this.dddk?.subtitle.showIndicator('processing', `${indicatorLabel} (${done}/${blocks.length})`);
    };

    await Promise.all(batches.map(async (batch) => {
      const inputs = batch.map((b) => b.html);
      const cached: (string | null)[] = inputs.map((html) => this.cacheGet(html, targetLang));
      const need = inputs.map((html, i) => (cached[i] == null ? html : null));
      const needIdxs = need.map((v, i) => (v != null ? i : -1)).filter((i) => i >= 0);
      let translations: string[] = [];
      if (needIdxs.length > 0) {
        const toSend = needIdxs.map((i) => inputs[i]!);
        try {
          translations = await this.translateBatch(toSend, targetLang);
        } catch (err) {
          console.warn('[immersive-translate] batch failed, skipping:', err);
          translations = toSend.map(() => '');
        }
      }
      const finalOutputs: string[] = cached.map((c) => c ?? '');
      let tIdx = 0;
      for (const i of needIdxs) {
        finalOutputs[i] = translations[tIdx] ?? '';
        if (finalOutputs[i]) this.cacheSet(inputs[i]!, targetLang, finalOutputs[i]!);
        tIdx++;
      }
      for (let i = 0; i < batch.length; i++) {
        const out = finalOutputs[i];
        if (out && out !== inputs[i]) {
          this.appendTranslation(batch[i]!.el, out);
        } else {
          batch[i]!.el.removeAttribute(TRANSLATED_ATTR);
        }
      }
      done += batch.length;
      updateProgress();
    }));

    this.dddk?.subtitle.hideIndicator();
    this.dddk?.subtitle.show({
      text: `Translation complete (${blocks.length} blocks)`,
      type: 'info',
      autoHide: 2000,
    });
  }

  /** Strip all injected translation nodes; revert page to original state. */
  disable(): void {
    if (!this.currentLang) return;
    this.cfg.root.querySelectorAll(`.${WRAPPER_CLASS}`).forEach((el) => el.remove());
    this.cfg.root.querySelectorAll(`[${TRANSLATED_ATTR}]`).forEach((el) => el.removeAttribute(TRANSLATED_ATTR));
    this.currentLang = null;
  }

  toggle(targetLang: string, label?: string): Promise<void> | void {
    if (this.isEnabled()) { this.disable(); return; }
    return this.enable(targetLang, label);
  }

  // ─── internals ───────────────────────────────────────────────

  private collectBlocks(): Array<{ el: HTMLElement; html: string }> {
    const out: Array<{ el: HTMLElement; html: string }> = [];
    const walker = document.createTreeWalker(this.cfg.root, NodeFilter.SHOW_ELEMENT, {
      acceptNode: (node) => {
        if (!(node instanceof HTMLElement)) return NodeFilter.FILTER_REJECT;
        if (this.effectiveIgnore && node.closest(this.effectiveIgnore)) return NodeFilter.FILTER_REJECT;
        if (!BLOCK_TAGS.has(node.tagName.toLowerCase())) return NodeFilter.FILTER_SKIP;
        if (node.hasAttribute(TRANSLATED_ATTR)) return NodeFilter.FILTER_REJECT;
        const text = (node.textContent ?? '').trim();
        if (text.length < 2) return NodeFilter.FILTER_REJECT;
        // Skip if it contains a block child (we'd double-translate).
        for (const child of Array.from(node.children)) {
          if (BLOCK_TAGS.has(child.tagName.toLowerCase())) return NodeFilter.FILTER_SKIP;
        }
        return NodeFilter.FILTER_ACCEPT;
      },
    });
    let n: Node | null;
    while ((n = walker.nextNode())) {
      const el = n as HTMLElement;
      el.setAttribute(TRANSLATED_ATTR, 'pending');
      // Use innerHTML so inline tags (<a>, <strong>, …) survive — the prompt
      // tells the LLM to preserve them.
      out.push({ el, html: (el.innerHTML ?? '').trim() });
    }
    return out;
  }

  private buildBatches(blocks: Array<{ el: HTMLElement; html: string }>):
    Array<Array<{ el: HTMLElement; html: string }>> {
    const out: Array<Array<{ el: HTMLElement; html: string }>> = [];
    let cur: Array<{ el: HTMLElement; html: string }> = [];
    let curChars = 0;
    for (const b of blocks) {
      if (cur.length >= this.cfg.batchSize || curChars + b.html.length > this.cfg.batchCharCap) {
        if (cur.length > 0) out.push(cur);
        cur = [];
        curChars = 0;
      }
      cur.push(b);
      curChars += b.html.length;
    }
    if (cur.length > 0) out.push(cur);
    return out;
  }

  private async translateBatch(blocks: string[], targetLang: string): Promise<string[]> {
    const llm = resolveLLM(this.cfg.llm, 'inline');
    const user = `Target language: ${targetLang}\n\nSource (JSON array of ${blocks.length} strings):\n${JSON.stringify(blocks)}`;
    const res = await llm.complete({
      messages: [
        { role: 'system', content: promptRegistry.render(PROMPT_IDS.TRANSLATE, undefined, this.currentLang ?? 'en') },
        { role: 'user', content: user },
      ],
      // Translation doesn't need reasoning — disable thinking for speed +
      // to keep the model from leaking analysis into the JSON output.
      thinking: 'off',
      jsonMode: true,
      temperature: 0.2,
      maxTokens: Math.min(4000, blocks.reduce((s, b) => s + b.length, 0) * 3 + 500),
    });
    const raw = res.content.trim();
    // Strip leading/trailing markdown fences if the model leaked them.
    const cleaned = stripCodeFence(raw);
    try {
      const parsed = JSON.parse(cleaned);
      const arr = extractTranslationsArray(parsed);
      if (!arr) throw new Error('no translations array found in response');
      if (arr.length !== blocks.length) throw new Error(`length mismatch ${arr.length} vs ${blocks.length}`);
      return arr.map((s: unknown) => String(s));
    } catch (err) {
      console.warn('[immersive-translate] JSON parse failed:', err, 'raw:', raw);
      return blocks.map(() => '');
    }
  }

  private appendTranslation(el: HTMLElement, html: string): void {
    el.setAttribute(TRANSLATED_ATTR, 'done');
    // Insert as a <font class="dddk-imm-translate"> after the original
    // content inside the same block — survives most rich-text + Office /
    // PDF HTML pipelines.
    const wrap = document.createElement(WRAPPER_TAG);
    wrap.className = WRAPPER_CLASS;
    wrap.innerHTML = html;
    // Force a line break before so original + translation are visually separable.
    const br = document.createElement('br');
    br.className = WRAPPER_CLASS;
    el.appendChild(br);
    el.appendChild(wrap);
  }

  private cacheKey(source: string, lang: string): string {
    return `imm:${lang}:${fnv1a(source)}`;
  }
  private cacheGet(source: string, lang: string): string | null {
    const k = this.cacheKey(source, lang);
    return (this.cfg.cache?.get(k)) ?? this.memCache.get(k) ?? null;
  }
  private cacheSet(source: string, lang: string, value: string): void {
    const k = this.cacheKey(source, lang);
    this.memCache.set(k, value);
    this.cfg.cache?.set(k, value);
  }
}

function ensureStyles(): void {
  injectScopedStyle(STYLE_ID, `
    .${WRAPPER_CLASS} {
      color: var(--dddk-text-muted, #6b6b6b);
      font-family: inherit;
      display: inline;
      opacity: 0.95;
    }
    br.${WRAPPER_CLASS} { display: block; margin-top: 4px; }
  `);
}

/**
 * Tolerantly extract the translations array from an LLM JSON response.
 *
 * `jsonMode: true` (OpenAI's `response_format: json_object`) forces a
 * top-level object, so the model can't return a bare array. We ask for
 * `{"translations": [...]}` but accept several common drift shapes:
 *   - `[...]` — model ignored jsonMode (rare).
 *   - `{"translations": [...]}` — the requested shape.
 *   - `{"<langCode>": [...]}` — model keyed by the target lang (`{"ja": [...]}`).
 *   - `{"result": [...]}` / `{"data": [...]}` — generic fallback keys.
 *   - Any object with exactly one array-valued property — use that array.
 */
function extractTranslationsArray(parsed: unknown): unknown[] | null {
  if (Array.isArray(parsed)) return parsed;
  if (!parsed || typeof parsed !== 'object') return null;
  const obj = parsed as Record<string, unknown>;
  if (Array.isArray(obj.translations)) return obj.translations;
  const arrayValues = Object.values(obj).filter((v) => Array.isArray(v)) as unknown[][];
  if (arrayValues.length === 1) return arrayValues[0]!;
  return null;
}

/** Tiny non-crypto hash for the translation cache key. 32-bit FNV-1a. */
function fnv1a(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h.toString(36);
}
